import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  ArrowRight,
  PenTool,
  Layers,
  Briefcase,
  Home,
  Ruler
} from 'lucide-react';

// Editorial Service Card Component - Reusable Pattern
const ServiceCard = ({ icon: Icon, title, description, route, delay }) => {
  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        component={RouterLink}
        to={route}
        sx={{ 
          height: '100%', 
          textAlign: 'center',
          textDecoration: 'none',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            borderColor: '#FF5722'
          }
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              color: 'white',
              boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
            }}
          >
            <Icon size={32} />
          </Box>
          
          <Typography 
            variant="h4" 
            component="h2" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              fontSize: '1.5rem',
              mb: 2,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="body1" 
            color="#666" 
            paragraph 
            sx={{ 
              mb: 4,
              lineHeight: 1.6,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {description}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FF5722' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                mr: 1,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Learn More
            </Typography>
            <ArrowRight size={16} />
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

const ServicesPage = () => {
  const theme = useTheme();
  const [showElements, setShowElements] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowElements(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const services = [
    {
      icon: PenTool,
      title: 'Interior Design',
      description: 'Complete interior design solutions that transform your space into a reflection of your style and personality.',
      route: '/services/interior-design'
    },
    {
      icon: Layers,
      title: '3D Visualization',
      description: 'Experience your space before it\'s built with photorealistic 3D renderings and virtual walkthroughs.',
      route: '/services/3d-visualization'
    },
    {
      icon: Briefcase,
      title: 'Commercial Design',
      description: 'Professional workspace design that enhances productivity and reflects your brand identity.',
      route: '/services/commercial-design'
    },
    {
      icon: Home,
      title: 'Residential Design',
      description: 'Create the perfect family home with designs that support your lifestyle and growing needs.',
      route: '/services/residential-design'
    },
    {
      icon: Ruler,
      title: 'Space Planning',
      description: 'Optimize your space layout for maximum functionality and efficient use of every square foot.',
      route: '/services/space-planning'
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden', background: '#FAFAFA' }}>
      {/* Hero Section - Editorial Pattern */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)',
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        {/* Background Geometric Elements - Reusable Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,87,34,0.03), transparent)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            background: 'linear-gradient(45deg, rgba(255,87,34,0.02), transparent)',
            transform: 'rotate(45deg)',
            zIndex: 0
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={8} textAlign="center">
              <Fade in={showElements} timeout={1000}>
                <Box>
                  {/* Badge Component - Reusable Pattern */}
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 3, 
                      py: 1, 
                      background: 'rgba(255,87,34,0.08)',
                      border: '1px solid rgba(255,87,34,0.2)',
                      borderRadius: 0,
                      mb: 4
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#FF5722"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: 2,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Our Services
                    </Typography>
                  </Box>
                  
                  {/* Headline Pattern - Reusable */}
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="#1A1A1A"
                    sx={{ 
                      fontSize: { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
                      fontWeight: 800,
                      lineHeight: 0.9,
                      mb: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Design{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Services
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      That{' '}
                      <Box 
                        component="span" 
                        sx={{ 
                          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        Transform
                      </Box>
                    </Box>
                  </Typography>
                  
                  {/* Subtitle Pattern - Reusable */}
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="#666"
                    sx={{ 
                      maxWidth: 800, 
                      mx: 'auto', 
                      mb: 8,
                      lineHeight: 1.7,
                      fontSize: '1.2rem',
                      position: 'relative',
                      pl: 4,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        width: 3,
                        height: 40,
                        background: 'linear-gradient(to bottom, #FF5722, #FF8A65)'
                      }
                    }}
                  >
                    From concept to completion, we offer comprehensive design services 
                    tailored to your unique vision and needs.
                  </Typography>

                  {/* CTA Buttons Pattern - Reusable */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowRight />}
                      sx={{
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(255,87,34,0.4)'
                        }
                      }}
                    >
                      Start Your Project
                    </Button>
                    
                    <Button
                      component={RouterLink}
                      to="/portfolio"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: '#1A1A1A',
                        color: '#1A1A1A',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 1,
                        borderWidth: 2,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#FF5722',
                          color: '#FF5722',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(255,87,34,0.2)'
                        }
                      }}
                    >
                      View Our Work
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Grid Section - Editorial Pattern */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          {/* Section Header Pattern - Reusable */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              color="#999" 
              sx={{ 
                mb: 2, 
                display: 'block',
                fontWeight: 500,
                letterSpacing: 3,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              What We Offer
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              color="#1A1A1A" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Our Design Services
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              color="#666" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Professional design solutions tailored to bring your vision to life.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <ServiceCard {...service} delay={300 * (index + 1)} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Section - Editorial Pattern */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="overline" 
                color="#999" 
                sx={{ 
                  mb: 2, 
                  display: 'block',
                  fontWeight: 500,
                  letterSpacing: 3,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Our Process
              </Typography>
              <Typography 
                variant="h2" 
                component="h2" 
                color="#1A1A1A" 
                sx={{ 
                  mb: 4,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                How We Work
              </Typography>
              <Typography 
                variant="body1" 
                color="#666" 
                paragraph
                sx={{ 
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Our design process is carefully crafted to ensure every project exceeds expectations. 
                From initial consultation to final installation, we work closely with you to bring 
                your vision to life.
              </Typography>
              <Typography 
                variant="body1" 
                color="#666" 
                paragraph
                sx={{ 
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Each project begins with understanding your needs, preferences, and lifestyle. 
                We then develop concepts that reflect your personality while maximizing functionality 
                and aesthetic appeal.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  { step: '01', title: 'Consultation', desc: 'Understanding your vision and requirements' },
                  { step: '02', title: 'Concept Design', desc: 'Creating initial designs and layouts' },
                  { step: '03', title: 'Development', desc: 'Refining details and selecting materials' },
                  { step: '04', title: 'Implementation', desc: 'Project management and installation' }
                ].map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
                      }}
                    >
                      {item.step}
                    </Box>
                    <Box>
                      <Typography 
                        variant="h6" 
                        color="#1A1A1A" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="#666"
                        sx={{ 
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section - Editorial Pattern */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Fade in={showElements}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h2" 
                component="h2" 
                color="white" 
                sx={{ 
                  mb: 3,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Ready to Get Started?
              </Typography>
              <Typography 
                variant="h5" 
                component="p" 
                color="white" 
                sx={{ 
                  mb: 6, 
                  opacity: 0.9,
                  fontWeight: 400,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Let's discuss your project and bring your vision to life.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowRight />}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': { 
                      background: 'rgba(255,255,255,0.3)'
                    }
                  }}
                >
                  Start Your Project
                </Button>
                
                <Button
                  component={RouterLink}
                  to="/portfolio"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  View Our Work
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default ServicesPage;